import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../../Requirement/components/common/ReusableTable";
import SearchFilter from "../../Requirement/components/common/SearchFilter";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import OfferDetails from "./component/OfferDetails";
import { Typography, TextField } from "@mui/material";
import dayjs from "dayjs";
import Requirements from "./RequirementTab";
import AlertModal from "../../Requirement/components/common/AlerModel";
import TrustScoreModal from "../../../components/common/TrustScoreModal";

const ReqShortlistedOffers = ({ requirementDetails }) => {
  const { setLoading, loading } = useLoading();
  const [offerList, setOfferList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [activeUser] = useState("");
  const [error, setError] = useState(null);
  const [INRToUsd] = useState(0.012);
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    deliveryDate: "",
    requirementDate: "",
    status: "",
  });
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // State for Verification Status Modal
  const [trustModal, setTrustModal] = useState(false);

  const trustModel = useCallback(() => {
    setTrustModal(true);
  }, []);

  const ShortlistedOffers = useCallback(
    async (searchValue,newRowStart = rowStart) => {
      setLoading(true);
      setError(null);
      try {
        const body = {
          target: "listGPExtendedRequirementsItems",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            CategoryType: "requirement",
            OfferStatusExtended: "Shortlist",
            GPEmailID: activeUser,
            Deleted: 0, // Only fetch non-deleted items
            ...(searchValue.requirementNumber && {
              RequirementNumber: searchValue.requirementNumber,
            }),
            ...(searchValue.requirementDate && {
              RequirementDate: "'" +  dayjs(searchValue.requirementDate).format("YYYY-MM-DD").toString() + "'",
            }),
            ...(searchValue.deliveryDate && {
              DeliveryDate: "'" +  dayjs(searchValue.deliveryDate).format("YYYY-MM-DD").toString() + "'",
            }),
          },
        };
        const response = await fetchData(body);
        if (Array.isArray(response.items) || Array.isArray(response)) {
          setOfferList(response.items || response || []);
          setTotalItems(response.total || 0);
        } else {
          throw new Error("Unexpected response format");
        }
      } catch (error) {
        console.error("Error loading matching offers:", error);
        setError("Failed to load matching offers. Please try again.");
        setOfferList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize, setLoading, activeUser]
  );

  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementNumber: value })),
      value: searchValues.requirementNumber,
    },
    {
      name: "deliveryDate",
      label: "Delivery Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, deliveryDate: value })),
      value: searchValues.deliveryDate,
    },
    {
      name: "requirementDate",
      label: "Requirement Date",
      type: "date",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, requirementDate: value })),
      value: searchValues.requirementDate,
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
      ShortlistedOffers(searchValues,newRowStart);
    },
    [pageSize]
  );

  const currentPage = Math.floor(rowStart / pageSize) + 1;
  const formatPrice = (value) => {
    if (!value || value === "") return "0.00";
    const numValue = parseFloat(value);
    return isNaN(numValue) ? "0.00" : numValue.toFixed(2);
  };

  const convertToUSD = (inrValue) => {
    if (!inrValue || inrValue === "") return "0.00";
    const usdValue = parseFloat(inrValue) * INRToUsd;
    return isNaN(usdValue) ? "0.00" : usdValue.toFixed(2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Requirement Number" },
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    {
      id: "Quantity",
      numeric: true,
      label: "Quantity (MT)",
      renderCell: (row) => formatPrice(row.Quantity),
    },
    {
      id: "FOBSalePrice",
      numeric: true,
      label: "FOB Sale Price",
      renderCell: (row) => row.FOBSalePrice,
    },
    { id: "Country", numeric: false, label: "Origin Country" },
    { id: "DestinationCountry", numeric: false, label: "Destination" },
    { id: "INCO", numeric: false, label: "INCO Terms" },
    { id: "OfferType", numeric: false, label: "Offer Type" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created Date",
      renderCell: (row) => formatDate(row.CreatedAt),
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires",
      renderCell: (row) => formatDate(row.Expires),
    },
    {
      id: "KYBStatus",
      numeric: false,
      label: "Seller Status",
      renderCell: (row) => {
        let bgColor = "";
        let textColor = "";

        switch (row.KYBStatus) {
          case "Approved":
            bgColor = "#d1fae5";
            textColor = "#065f46";
            break;
          default:
            bgColor = "#fef3c7";
            textColor = "#b45309";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              padding: "4px 10px",
              borderRadius: "999px",
              fontSize: "0.75rem",
              fontWeight: 500,
              display: "inline-block",
            }}
          >
            {row.KYBStatus === "Approved" ? "Verified" : "Unverified"}
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    ShortlistedOffers(searchValues);
  }, [ShortlistedOffers,searchValues]);

  const handleRowClick = useCallback((row) => {
    setSelectedOffer(row);
    console.log("row", row);
  });

  const handleSearch = () => {
    setRowStart(0);
    ShortlistedOffers(searchValues,0);
  };
  const handleRemoveOffer = async (offer) => { 
    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message:
        "Are you sure you want to remove this offer from your shortlist?",
      isConfirm: true,
      onConfirm: async () => {
        setLoading(true);
        setError(null); // Clear any existing errors
        try {
          const body = {
            target: "gpExtendedOfferUpdate",
            GPOfferExtendedInput: {
              GPOfferExtendedID: offer.GPOfferExtendedID,
              Deleted: 1,
            },
          };
          if (body !== null) {
            const result = await postQuery(body, "backendops");

            if (result) {
              // Clear the selected offer
              setSelectedOffer(null);
              // Reload the table
              await ShortlistedOffers(rowStart);
              console.log("Offer removed successfully");
            }
          }
        } catch (error) {
          console.error("Error removing offer:", error);
          setModal({
            show: true,
            type: "error",
            title: "Error",
            message: "Failed to remove from shortlist offer. Please try again.",
            autoClose: false,
          });
        } finally {
          setLoading(false);
        }
        setModal((prev) => ({ ...prev, show: false }));
      },
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };
  const handleRemoveOffer3 = async (offer) => {
    if (
      !window.confirm(
        "Are you sure you want to remove this offer from your shortlist?"
      )
    ) {
      return;
    }
    setLoading(true);
    console.log("handleRemoveOffer triggered");
    console.log("removeOffer::", offer);
    try {
      // Simulate API call with a 1-second delay
      let body = null;
      console.log("Saving data to API:", offer);

      body = {
        target: "gpExtendedOfferUpdate",
        GPOfferExtendedInput: {
          GPOfferExtendedID: offer.GPOfferExtendedID,
          Deleted: 1,
        },
      };

      console.log("Saving body to API:", body);
      if (body !== null) {
        const result = await postQuery(body, "backendops");
        console.log("result@", result);
        if (result) {
          setModal({
            show: true,
            type: "success",
            title: "Success",
            message: `Offer removed successfully!`,
            autoClose: false,
            nextAction: () => {
              // sendTrueToParent();
              setSelectedOffer(null);
            },
          });
        }
      }

      // Refresh the table data
      setSelectedOffer(null);
      // Reload the table
      await ShortlistedOffers(rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to remove from shortlist offer. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const Button = ({ label, onClick }) => {
    return (
      <button className="btn-action-re" onClick={onClick}>
        {label}
      </button>
    );
  };

  // Pass the selectedOffer ID to DataTable via selectedRows
  const selectedRows = selectedOffer
    ? [selectedOffer.GPOfferID || selectedOffer.id]
    : [];

  return (
    <div className="matching-offers-container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Shortlisted Offers</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearch} />
        </div>
      </div>
      {/* <div className="table-header">
        <div className="header-text">
          <div className="head-text">Shortlisted Offers</div>
        </div>
        <div className="head-btn">
        </div>
      </div>
      <div className="search-filter row">
        <SearchFilter fields={fields} onSearch={handleSearch} />
      </div> */}
      {loading && <div className="loading">Loading offers...</div>}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && offerList.length === 0 && (
        <div className="empty-state">No matching offers found.</div>
      )}
      {!loading && offerList.length > 0 && (
        <DataTable
          columns={columns}
          data={offerList}
          total={totalItems}
          title="Matching Offers Information"
          rowsPerPage={pageSize}
          onPageChange={handlePageChange}
          currentPage={currentPage}
          onRowClick={handleRowClick}
          selectedRows={selectedRows} // Pass selected row ID
        />
      )}
      {selectedOffer && (
        <>
          {/* Offer Details Section */}
          <div className="offer-detail1">
            <div className="header-text-re">
              <div className="head-text">Offer Details</div>
              <div className="button-group">
                <Button label="Verification Status" onClick={trustModel} />
                <Button
                  label="Remove"
                  onClick={() => handleRemoveOffer(selectedOffer)}
                />
              </div>
            </div>
            {
              <OfferDetails
                offer={selectedOffer}
                btnShortlist={false}
                btnSendQuery={false}
                btnRequestQuote={false}
                btnRequestSpecifications={false}
                btnRequestBestPrice={false}
                btnNegotiate={false}
                btnRequestShippingDetails={false}
                btnLogisticsSupport={false}
                btnRaiseQuery={true}
              />
            }
          </div>

          {/* Requirement Details Section */}
          {selectedOffer && selectedOffer.RequirementsID && (
            <div className="requirement-detail" style={{ marginTop: "20px" }}>
              <div className="header-text-re">
                <div className="head-text">
                  Requirement - {selectedOffer?.RequirementNumber || ""}
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Typography variant="body2">Product</Typography>
                  <TextField
                    value={selectedOffer?.ProductName || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <Typography variant="body2">Grade</Typography>
                  <TextField
                    value={selectedOffer?.Grade || ""}
                    InputProps={{ readOnly: true }}
                    sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                    size="small"
                  />
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Origin</Typography>
                    <TextField
                      value={selectedOffer.OriginCountry || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Destination</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryLocation
                          ? selectedOffer.DeliveryLocation.split("~")[0]
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Quantity (MT)</Typography>
                    <TextField
                      value={selectedOffer.RqQuantity || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Inco Terms</Typography>
                    <TextField
                      value={selectedOffer.Incoterm || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Payment Terms</Typography>
                    <TextField
                      value={selectedOffer.PaymentTerms || selectedOffer.PreferredPaymentMethod || ""}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Packaging</Typography>
                    <TextField
                      value={(() => {
                        const packaging = selectedOffer.ProductPackaging || "";
                        if (packaging.includes("item=")) {
                          const match = packaging.match(/item=([^,}]+)/);
                          return match ? match[1].trim() : packaging;
                        }
                        return packaging;
                      })()}
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery Port</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryLocation
                          ? selectedOffer.DeliveryLocation.split("~")[1]
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (Start)</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryPeriodFrom
                          ? dayjs(selectedOffer.DeliveryPeriodFrom).format(
                              "DD-MM-YYYY"
                            )
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Delivery (End)</Typography>
                    <TextField
                      value={
                        selectedOffer.DeliveryPeriodTill
                          ? dayjs(selectedOffer.DeliveryPeriodTill).format(
                              "DD-MM-YYYY"
                            )
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Valid Until</Typography>
                    <TextField
                      value={
                        selectedOffer.ValidUntil
                          ? dayjs(selectedOffer.ValidUntil).format("DD-MM-YYYY")
                          : ""
                      }
                      InputProps={{ readOnly: true }}
                      sx={{ backgroundColor: "#f5f5f5", width: "100%" }}
                      size="small"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {/* Verification Status Modal */}
      {trustModal && selectedOffer && (
        <TrustScoreModal
          onClose={() => setTrustModal(false)}
          userEmail={selectedOffer.SellerEmailID}
        />
      )}
    </div>
  );
};

export default ReqShortlistedOffers;
